import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useServerStore } from '../../store/serverStore';
import { useAuthStore } from '../../store/authStore';
import { useFriendStore } from '../../store/friendStore';
import { useMessageStore } from '../../store/messageStore';
import { useVoiceStore, type VoiceUser } from '../../store/voiceStore';
import { ChevronRight, Hash, Plus, Settings, Users, LogOut, Volume2, Home as HomeIcon, Mic, MicOff, Shield, Gamepad2, Link2, DoorOpen, Search, PenSquare, X, ChevronDown, Copy, Lock, Trash2 } from 'lucide-react';
import { useGamingStore } from '../../store/gamingStore';
import { useAdminStore } from '../../store/adminStore';
import { Permissions, computeEffectivePermissions, hasPermission } from '../../lib/permissions';
import { useDeveloperMode } from '../../lib/developerMode';
import ServerItem from '../server/ServerItem';
import CreateServerModal from '../server/CreateServerModal';
import UserSettingsModal from '../settings/UserSettingsModal';
import ServerSettingsModal from '../server/ServerSettingsModal';
import RoleManagementModal from '../server/RoleManagementModal';
import CreateInviteModal from '../invitation/CreateInviteModal';
import CreateChannelModal from '../server/CreateChannelModal';
import EditChannelModal from '../server/EditChannelModal';
import DeleteChannelModal from '../server/DeleteChannelModal';
import ContextMenu, { ContextMenuItem } from '../ui/ContextMenu';
import VoiceBar from '../voice/VoiceBar';
import { Skeleton } from '../ui/Skeleton';

interface SidebarProps {
  onToggleMembers?: () => void;
}

type SidebarDropTarget =
  | { scope: 'topLevel'; index: number }
  | { scope: 'category'; parentId: string; index: number };

const Sidebar: React.FC<SidebarProps> = ({ onToggleMembers }) => {
  const { servers, currentServer, channels, currentChannel, setCurrentServer, fetchUserServers, isLoading, setCurrentChannel, members, leaveServer, reorderChannels, deleteChannel } = useServerStore();
  const { signOut, profile, user } = useAuthStore();
  const isAdmin = user?.is_admin || localStorage.getItem('is_admin') === '1';
  const { friends, fetchFriends } = useFriendStore();
  const { setCurrentDmUser, currentDmUser, unreadDms, conversations, fetchConversations } = useMessageStore();
  const {
    isConnected: isVoiceConnected,
    isMuted: isVoiceMuted,
    voiceChannelParticipants,
    currentChannelId: voiceChannelId,
    userSpeakingStates,
    fetchVisibleVoiceChannelParticipants,
  } = useVoiceStore();
  const [showCreateServer, setShowCreateServer] = useState(false);
  const [showUserSettings, setShowUserSettings] = useState(false);
  const [showServerSettings, setShowServerSettings] = useState(false);
  const [showRoleManagement, setShowRoleManagement] = useState(false);
  const { isAdminConnectActive, setAdminConnectActive } = useAdminStore();
  const [showCreateInvite, setShowCreateInvite] = useState(false);
  const [createChannelDraft, setCreateChannelDraft] = useState<{ type?: 'text' | 'voice' | 'category'; parentId?: string | null } | null>(null);
  const [editingChannelId, setEditingChannelId] = useState<string | null>(null);
  const [deletingChannelId, setDeletingChannelId] = useState<string | null>(null);
  const [serverContextMenu, setServerContextMenu] = useState<{ position: { x: number; y: number }; serverId: string } | null>(null);
  const [channelContextMenu, setChannelContextMenu] = useState<{ position: { x: number; y: number }; channelId: string | null } | null>(null);
  const [activeSection, setActiveSection] = useState<'servers' | 'friends'>('friends');
  const { isGamingHubActive, setGamingHubActive } = useGamingStore();
  const developerMode = useDeveloperMode();
  const [isElectron] = useState(() => !!window.api);
  const [isMac] = useState(() => navigator.platform.toUpperCase().includes('MAC'));
  const [showServerDropdown, setShowServerDropdown] = useState(false);
  const [dmSearch, setDmSearch] = useState('');
  const [showNewDm, setShowNewDm] = useState(false);
  const [newDmUsername, setNewDmUsername] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({});
  const [draggedChannelId, setDraggedChannelId] = useState<string | null>(null);
  const [dropTarget, setDropTarget] = useState<SidebarDropTarget | null>(null);
  const draggedChannelIdRef = useRef<string | null>(null);
  const dropTargetRef = useRef<SidebarDropTarget | null>(null);
  const pointerDragRef = useRef<{ id: string; startX: number; startY: number; active: boolean } | null>(null);
  const justCompletedPointerDragRef = useRef(false);

  useEffect(() => {
    fetchUserServers();
    fetchFriends();
    fetchConversations();
  }, [fetchUserServers, fetchFriends, fetchConversations]);

  useEffect(() => {
    if (currentServer && activeSection !== 'servers') {
      setActiveSection('servers');
      setGamingHubActive(false);
      setCurrentDmUser(null);
    }
  }, [currentServer, activeSection, setGamingHubActive, setCurrentDmUser]);

  const handleServerClick = (server: any) => {
    console.log("Serveur sélectionné:", server.name);
    setCurrentDmUser(null);
    setCurrentServer(server);
    setActiveSection('servers');
    setGamingHubActive(false);
    // On remet currentChannel à null pour déclencher l'effet ci-dessus
    setCurrentChannel(null);
  };

  const handleFriendClick = (friendId: string) => {
    setCurrentChannel(null);
    setCurrentServer(null);
    setCurrentDmUser(friendId);
    setActiveSection('friends');
  };

  // Check if user is the server owner
  const isServerOwner = currentServer?.owner_id === profile?.id;

  // Filtered conversations for DM search
  const filteredConversations = useMemo(() => {
    if (!dmSearch.trim()) return conversations;
    return conversations.filter(c => c.username.toLowerCase().includes(dmSearch.toLowerCase()));
  }, [conversations, dmSearch]);

  // Get online status from friends list
  const friendStatusMap = useMemo(() => {
    const map: Record<string, string> = {};
    friends.forEach(f => {
      if (f.friend) {
        map[f.friend_id] = f.friend.status || 'offline';
      }
    });
    return map;
  }, [friends]);

  // Handle sending new DM
  const handleNewDm = () => {
    if (!newDmUsername.trim()) return;
    const friend = friends.find(f => f.friend?.username?.toLowerCase() === newDmUsername.toLowerCase());
    if (friend) {
      handleFriendClick(friend.friend_id);
      setNewDmUsername('');
      setShowNewDm(false);
    }
  };

  // Compute effective permissions — show settings gear if user has any management permission
  const currentMember = members.find((m) => m.user_id === profile?.id);
  const effectivePerms = computeEffectivePermissions(
    isServerOwner,
    ((currentMember as any)?.roles || []) as { permissions: number }[],
  );
  const managementPerms = Permissions.MANAGE_CHANNELS | Permissions.MANAGE_ROLES | Permissions.MANAGE_SERVER | Permissions.KICK_MEMBERS | Permissions.BAN_MEMBERS;
  const canAccessSettings = isServerOwner || (effectivePerms & managementPerms) !== 0;
  const canManageChannels = hasPermission(effectivePerms, Permissions.MANAGE_CHANNELS);
  const canCreateInvite = hasPermission(effectivePerms, Permissions.CREATE_INVITE);

  const openChannelContextMenu = (event: React.MouseEvent, channelId: string | null = null) => {
    if (!canManageChannels && !(developerMode && channelId)) return;
    event.preventDefault();
    event.stopPropagation();
    setChannelContextMenu({ position: { x: event.clientX, y: event.clientY }, channelId });
  };

  const categories = useMemo(
    () => channels.filter(channel => channel.type === 'category').sort((a: any, b: any) => (a.position || 0) - (b.position || 0)),
    [channels],
  );
  const topLevelItems = useMemo(
    () => channels
      .filter(channel => !channel.parent_id)
      .sort((a: any, b: any) => ((a.position || 0) - (b.position || 0)) || String(a.created_at || '').localeCompare(String(b.created_at || ''))),
    [channels],
  );
  const channelsByCategory = useMemo(() => {
    const grouped: Record<string, typeof channels> = {};
    categories.forEach((category) => {
      grouped[category.id] = channels
        .filter(channel => channel.type !== 'category' && channel.parent_id === category.id)
        .sort((a: any, b: any) => (a.position || 0) - (b.position || 0));
    });
    return grouped;
  }, [categories, channels]);
  const voiceChannelIds = useMemo(
    () => channels.filter((channel) => channel.type === 'voice').map((channel) => channel.id),
    [channels],
  );

  const buildReorderItems = (nextChannels: typeof channels, syncChannelId?: string) =>
    nextChannels.map((channel: any) => ({
      id: channel.id,
      parent_id: channel.parent_id || null,
      position: channel.position || 0,
      sync_permissions: syncChannelId === channel.id,
    }));

  const sortChannelsForSidebar = (items: typeof channels) =>
    [...items].sort((a: any, b: any) => ((a.position || 0) - (b.position || 0)) || String(a.created_at || '').localeCompare(String(b.created_at || '')));

  const clampIndex = (index: number, length: number) => Math.max(0, Math.min(index, length));

  const getAdjustedInsertionIndex = (sourceIndex: number, targetIndex: number) =>
    sourceIndex >= 0 && targetIndex > sourceIndex ? targetIndex - 1 : targetIndex;

  const reindexTopLevel = (next: typeof channels, orderedItems?: typeof channels) => {
    const ordered = orderedItems || sortChannelsForSidebar(next.filter((channel) => !channel.parent_id));
    ordered.forEach((channel: any, index) => {
      const match = next.find((candidate) => candidate.id === channel.id);
      if (match) {
        match.parent_id = null;
        match.position = index;
      }
    });
  };

  const reindexCategoryChildren = (next: typeof channels, parentId: string, orderedItems?: typeof channels) => {
    const ordered = orderedItems || sortChannelsForSidebar(next.filter((channel) => channel.type !== 'category' && channel.parent_id === parentId));
    ordered.forEach((channel: any, index) => {
      const match = next.find((candidate) => candidate.id === channel.id);
      if (match) {
        match.parent_id = parentId;
        match.position = index;
      }
    });
  };

  const moveChannel = async (sourceId: string, target: SidebarDropTarget) => {
    if (!currentServer) return;
    const source = channels.find((channel) => channel.id === sourceId);
    if (!source || source.type === 'category') return;
    const previousParentId = source.parent_id || null;
    const next = channels.map((channel: any) => ({ ...channel }));
    const sourceCopy = next.find((channel) => channel.id === sourceId);
    if (!sourceCopy) return;

    if (target.scope === 'topLevel') {
      const topItems = sortChannelsForSidebar(next.filter((channel) => !channel.parent_id && channel.id !== sourceId));
      const previousTopItems = sortChannelsForSidebar(channels.filter((channel) => !channel.parent_id));
      const sourceTopIndex = previousParentId === null ? previousTopItems.findIndex((channel) => channel.id === sourceId) : -1;
      const insertIndex = previousParentId === null
        ? getAdjustedInsertionIndex(sourceTopIndex, target.index)
        : target.index;
      sourceCopy.parent_id = null;
      topItems.splice(clampIndex(insertIndex, topItems.length), 0, sourceCopy);
      reindexTopLevel(next, topItems);
      if (previousParentId) reindexCategoryChildren(next, previousParentId);
    } else {
      const targetChildren = sortChannelsForSidebar(next
        .filter((channel) => channel.type !== 'category' && channel.parent_id === target.parentId && channel.id !== sourceId));
      const previousTargetChildren = sortChannelsForSidebar(channels
        .filter((channel) => channel.type !== 'category' && channel.parent_id === target.parentId));
      const sourceChildIndex = previousParentId === target.parentId
        ? previousTargetChildren.findIndex((channel) => channel.id === sourceId)
        : -1;
      const insertIndex = previousParentId === target.parentId
        ? getAdjustedInsertionIndex(sourceChildIndex, target.index)
        : target.index;
      sourceCopy.parent_id = target.parentId;
      targetChildren.splice(clampIndex(insertIndex, targetChildren.length), 0, sourceCopy);
      reindexCategoryChildren(next, target.parentId, targetChildren);
      if (previousParentId === null) reindexTopLevel(next);
      if (previousParentId && previousParentId !== target.parentId) reindexCategoryChildren(next, previousParentId);
    }

    let syncPermissions = false;
    if (target.scope === 'category' && target.parentId !== previousParentId) {
      syncPermissions = window.confirm('Sync this channel permissions with the category? Choose Cancel to keep current permissions.');
    }
    await reorderChannels(currentServer.id, buildReorderItems(next, syncPermissions ? sourceId : undefined));
  };

  const moveTopLevelItem = async (sourceId: string, targetIndex: number) => {
    if (!currentServer) return;
    const next = channels.map((channel: any) => ({ ...channel }));
    const source = next.find((channel) => channel.id === sourceId);
    if (!source) return;
    const orderedTopLevelItems = sortChannelsForSidebar(next.filter((channel) => !channel.parent_id && channel.id !== sourceId));
    const previousTopLevelItems = sortChannelsForSidebar(channels.filter((channel) => !channel.parent_id));
    const sourceIndex = previousTopLevelItems.findIndex((channel) => channel.id === sourceId);
    if (sourceIndex < 0) return;
    source.parent_id = null;
    orderedTopLevelItems.splice(clampIndex(getAdjustedInsertionIndex(sourceIndex, targetIndex), orderedTopLevelItems.length), 0, source);
    reindexTopLevel(next, orderedTopLevelItems);
    await reorderChannels(currentServer.id, buildReorderItems(next));
  };

  const clearDragState = () => {
    draggedChannelIdRef.current = null;
    dropTargetRef.current = null;
    pointerDragRef.current = null;
    setDraggedChannelId(null);
    setDropTarget(null);
  };

  const startChannelDrag = (id: string) => {
    draggedChannelIdRef.current = id;
    setDraggedChannelId(id);
  };

  const updateDropTarget = (target: SidebarDropTarget) => {
    dropTargetRef.current = target;
    setDropTarget(target);
  };

  const DropIndicator = ({ visible }: { visible: boolean }) => (
    <div className={`px-2 transition-all duration-150 ${visible ? 'h-2 opacity-100' : 'h-0 opacity-0'}`}>
      <div className="h-0.5 rounded-full bg-accent-color shadow-[0_0_10px_rgba(249,115,22,0.65)]" />
    </div>
  );

  const getDraggedItem = (event?: React.DragEvent) => {
    const transferId = event?.dataTransfer?.getData('application/x-erozion-channel-id') || null;
    const draggedId = draggedChannelIdRef.current ?? draggedChannelId ?? transferId;
    return draggedId ? channels.find((channel) => channel.id === draggedId) : null;
  };

  const handleDropToTopLevel = (index: number, event?: React.DragEvent) => {
    const dragged = getDraggedItem(event);
    if (!dragged) return;
    if (dragged.type === 'category') {
      moveTopLevelItem(dragged.id, index);
    } else {
      moveChannel(dragged.id, { scope: 'topLevel', index });
    }
  };

  const handleDropToCategory = (parentId: string, index: number, event?: React.DragEvent) => {
    const dragged = getDraggedItem(event);
    if (!dragged || dragged.type === 'category') return;
    moveChannel(dragged.id, { scope: 'category', parentId, index });
  };

  const updateTopLevelTargetFromRow = (event: React.DragEvent, index: number) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const isAfter = event.clientY > rect.top + rect.height / 2;
    updateDropTarget({ scope: 'topLevel', index: index + (isAfter ? 1 : 0) });
  };

  const updateCategoryTargetFromRow = (event: React.DragEvent, parentId: string, index: number) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const isAfter = event.clientY > rect.top + rect.height / 2;
    updateDropTarget({ scope: 'category', parentId, index: index + (isAfter ? 1 : 0) });
  };

  const TopLevelDropSlot = ({ index }: { index: number }) => {
    const visible = dropTarget?.scope === 'topLevel' && dropTarget.index === index;
    const dragging = Boolean(draggedChannelId);
    return (
      <div
        data-drop-scope="topLevel"
        data-drop-index={index}
        className={dragging ? 'min-h-2 rounded-lg' : ''}
        onDragOver={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!getDraggedItem()) return;
          updateDropTarget({ scope: 'topLevel', index });
        }}
        onDrop={(event) => {
          event.preventDefault();
          event.stopPropagation();
          handleDropToTopLevel(index, event);
          clearDragState();
        }}
      >
        <DropIndicator visible={visible} />
      </div>
    );
  };

  const beginPointerDrag = (event: React.MouseEvent, id: string) => {
    if (!canManageChannels || event.button !== 0) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('button, input, textarea, select, [data-no-channel-drag="true"]')) return;
    pointerDragRef.current = { id, startX: event.clientX, startY: event.clientY, active: false };
  };

  const targetFromElement = (element: Element | null, clientY: number): SidebarDropTarget | null => {
    if (!element) return null;
    const rowElement = element.closest<HTMLElement>('[data-channel-row="true"]');
    if (rowElement) {
      const index = Number(rowElement.dataset.index || 0);
      const rect = rowElement.getBoundingClientRect();
      const isAfter = clientY > rect.top + rect.height / 2;
      if (rowElement.dataset.parentId) {
        return { scope: 'category', parentId: rowElement.dataset.parentId, index: index + (isAfter ? 1 : 0) };
      }
      return { scope: 'topLevel', index: index + (isAfter ? 1 : 0) };
    }

    const dropElement = element.closest<HTMLElement>('[data-drop-scope]');
    if (dropElement) {
      const scope = dropElement.dataset.dropScope;
      const index = Number(dropElement.dataset.dropIndex || 0);
      if (scope === 'topLevel') return { scope: 'topLevel', index };
      if (scope === 'category' && dropElement.dataset.parentId) {
        return { scope: 'category', parentId: dropElement.dataset.parentId, index };
      }
    }
    return null;
  };

  useEffect(() => {
    const handlePointerMove = (event: MouseEvent) => {
      const drag = pointerDragRef.current;
      if (!drag) return;
      const deltaX = Math.abs(event.clientX - drag.startX);
      const deltaY = Math.abs(event.clientY - drag.startY);
      if (!drag.active) {
        if (deltaX < 4 && deltaY < 4) return;
        drag.active = true;
        startChannelDrag(drag.id);
      }
      event.preventDefault();
      const target = targetFromElement(document.elementFromPoint(event.clientX, event.clientY), event.clientY);
      if (target) updateDropTarget(target);
    };

    const handlePointerUp = () => {
      const drag = pointerDragRef.current;
      if (!drag) return;
      const target = dropTargetRef.current;
      const wasActive = drag.active;
      const draggedId = drag.id;
      if (wasActive) {
        justCompletedPointerDragRef.current = true;
        window.setTimeout(() => { justCompletedPointerDragRef.current = false; }, 0);
      }
      if (wasActive && target) {
        const dragged = channels.find((channel) => channel.id === draggedId);
        if (dragged) {
          if (target.scope === 'topLevel') {
            if (dragged.type === 'category') {
              moveTopLevelItem(dragged.id, target.index);
            } else {
              moveChannel(dragged.id, target);
            }
          } else if (dragged.type !== 'category') {
            moveChannel(dragged.id, target);
          }
        }
      }
      clearDragState();
    };

    window.addEventListener('mousemove', handlePointerMove);
    window.addEventListener('mouseup', handlePointerUp);
    return () => {
      window.removeEventListener('mousemove', handlePointerMove);
      window.removeEventListener('mouseup', handlePointerUp);
    };
  }, [channels, dropTarget, canManageChannels]);

  useEffect(() => {
    if (activeSection !== 'servers' || !currentServer || voiceChannelIds.length === 0) return;

    fetchVisibleVoiceChannelParticipants(voiceChannelIds);
    const interval = window.setInterval(() => {
      fetchVisibleVoiceChannelParticipants(voiceChannelIds);
    }, 5000);

    return () => window.clearInterval(interval);
  }, [activeSection, currentServer?.id, voiceChannelIds, fetchVisibleVoiceChannelParticipants]);

  const renderChannelRow = (channel: any, index: number, parentId: string | null = null) => {
    const isVoice = channel.type === 'voice';
    const participants: VoiceUser[] = isVoice ? (voiceChannelParticipants[channel.id] ?? []) : [];
    const isActiveVoice = voiceChannelId === channel.id;
    const canConnectVoice = hasPermission(channel.permissions || 0, Permissions.CONNECT_VOICE);
    const shouldInjectSelf = Boolean(
      isActiveVoice &&
      isVoiceConnected &&
      profile?.id &&
      !participants.some((participant) => participant.id === profile.id),
    );
    const displayedParticipants: VoiceUser[] = shouldInjectSelf
      ? [
          {
            id: profile!.id,
            username: `${profile!.display_name || profile!.username || 'Vous'} (Vous)`,
            avatar_url: profile!.avatar_url || null,
            muted: isVoiceMuted,
          },
          ...participants,
        ]
      : participants;
    return (
      <div key={channel.id}>
        <DropIndicator visible={Boolean(parentId && dropTarget?.scope === 'category' && dropTarget.parentId === parentId && dropTarget.index === index)} />
        <div
          data-channel-row="true"
          data-channel-id={channel.id}
          data-index={index}
          data-parent-id={parentId || undefined}
          className={`flex items-center px-2.5 py-1.5 rounded-lg transition-all duration-150 group ${
            currentChannel?.id === channel.id
              ? 'bg-highlight text-text-primary'
              : isVoice && !canConnectVoice
                ? 'text-text-muted/60 cursor-not-allowed opacity-70'
                : 'text-text-secondary hover:bg-highlight/30 hover:text-text-primary cursor-pointer'
          } ${canManageChannels ? 'w-full select-none cursor-grab active:cursor-grabbing' : ''} ${draggedChannelId === channel.id ? 'opacity-60' : ''}`}
          onMouseDown={(event) => beginPointerDrag(event, channel.id)}
          onDragStart={(event) => {
            event.preventDefault();
          }}
          onContextMenu={(e) => openChannelContextMenu(e, channel.id)}
          onClick={(event) => {
            if (justCompletedPointerDragRef.current) {
              event.preventDefault();
              event.stopPropagation();
              return;
            }
            if (isVoice && !canConnectVoice) return;
            setCurrentDmUser(null);
            setCurrentChannel(channel);
          }}
          title={isVoice && !canConnectVoice ? "You don't have permission to connect to this voice channel." : undefined}
        >
          {isVoice ? (
            canConnectVoice ? (
              <Volume2 className={`h-4 w-4 mr-2 flex-shrink-0 ${isActiveVoice ? 'text-success' : 'text-text-muted'}`} />
            ) : (
              <Lock className="h-4 w-4 mr-2 flex-shrink-0 text-text-muted" />
            )
          ) : (
            <Hash className="h-4 w-4 mr-2 flex-shrink-0 text-text-muted" />
          )}
          <span className="truncate flex-1 text-sm font-medium">{channel.name}</span>
          {displayedParticipants.length > 0 && (
            <span className="text-[10px] text-text-muted bg-highlight/50 px-1.5 py-0.5 rounded-full ml-1">{displayedParticipants.length}</span>
          )}
        </div>
        {displayedParticipants.length > 0 && (
          <div className="ml-4 mt-0.5 space-y-px border-l-2 border-border-color/50 pl-2">
            {displayedParticipants.map((u) => {
              const speakingLevel = userSpeakingStates[u.id] ?? 0;
              const isSpeaking = speakingLevel > 0.15;
              return (
                <div key={u.id} className="flex items-center gap-1.5 px-1.5 py-1 rounded-md group/user hover:bg-highlight/20 transition-colors">
                  <div className={`relative w-5 h-5 rounded-full flex-shrink-0 overflow-hidden transition-all ${isSpeaking ? 'ring-2 ring-success ring-offset-1 ring-offset-bg-secondary' : ''}`}>
                    {u.avatar_url ? (
                      <img src={u.avatar_url} alt={u.username} className="w-5 h-5 object-cover" />
                    ) : (
                      <div className="w-5 h-5 bg-accent-color flex items-center justify-center">
                        <span className="text-[8px] font-bold text-white">{u.username.charAt(0).toUpperCase()}</span>
                      </div>
                    )}
                  </div>
                  <span className={`text-xs truncate flex-1 ${isSpeaking ? 'text-success font-medium' : 'text-text-secondary'}`}>{u.username}</span>
                  {u.muted
                    ? <MicOff className="h-3 w-3 text-error flex-shrink-0" />
                    : isSpeaking
                      ? <div className="w-3 h-3 flex-shrink-0 rounded-full bg-success/20 flex items-center justify-center"><div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" /></div>
                      : <Mic className="h-3 w-3 text-text-muted flex-shrink-0 opacity-0 group-hover/user:opacity-100 transition-opacity" />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderCategoryBlock = (category: any, topLevelIndex: number, topLevelCount: number) => {
    const children = channelsByCategory[category.id] || [];
    const collapsed = collapsedCategories[category.id];
    if (children.length === 0 && !canManageChannels) return null;
    const hasTopLevelItemAbove = topLevelIndex > 0;
    const hasTopLevelItemBelow = topLevelIndex < topLevelCount - 1;

    return (
      <div
        key={category.id}
        className={`${hasTopLevelItemAbove ? 'pt-4' : 'pt-2'} ${hasTopLevelItemBelow ? 'pb-3' : 'pb-1'}`}
      >
        <div
          data-channel-row="true"
          data-channel-id={category.id}
          data-index={topLevelIndex}
          className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-text-muted hover:text-text-secondary hover:bg-highlight/20 cursor-pointer group/category ${canManageChannels ? 'w-full select-none cursor-grab active:cursor-grabbing' : ''}`}
          onMouseDown={(event) => beginPointerDrag(event, category.id)}
          onDragStart={(event) => {
            event.preventDefault();
          }}
          onContextMenu={(e) => openChannelContextMenu(e, category.id)}
          onClick={(event) => {
            if (justCompletedPointerDragRef.current) {
              event.preventDefault();
              event.stopPropagation();
              return;
            }
            setCollapsedCategories((current) => ({ ...current, [category.id]: !current[category.id] }));
          }}
        >
          {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          <span className="min-w-0 flex-1 truncate text-[10px] font-bold uppercase tracking-[0.08em]">{category.name}</span>
          {canManageChannels && (
            <button
              data-no-channel-drag="true"
              className="opacity-0 group-hover/category:opacity-100 w-4 h-4 flex items-center justify-center hover:text-text-primary"
              onClick={(event) => {
                event.stopPropagation();
                setCreateChannelDraft({ type: 'text', parentId: category.id });
              }}
            >
              <Plus className="h-3 w-3" />
            </button>
          )}
        </div>
        {!collapsed && (
          <div
            data-drop-scope="category"
            data-parent-id={category.id}
            data-drop-index={children.length}
            className={`space-y-0.5 ${children.length === 0 && draggedChannelId ? 'min-h-8 rounded-lg' : ''}`}
            onDragOver={(event) => {
              event.preventDefault();
              event.stopPropagation();
              const dragged = getDraggedItem();
              if (!dragged || dragged.type === 'category') return;
              updateDropTarget({ scope: 'category', parentId: category.id, index: children.length });
            }}
            onDrop={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleDropToCategory(category.id, children.length, event);
              clearDragState();
            }}
          >
            {children.map((channel, index) => renderChannelRow(channel, index, category.id))}
            <DropIndicator visible={dropTarget?.scope === 'category' && dropTarget.parentId === category.id && dropTarget.index === children.length} />
          </div>
        )}
      </div>
    );
  };

  return (
    <aside className="flex h-full">
      {/* Navigation sidebar — icon bar */}
      <div className="w-[72px] bg-bg-primary flex flex-col items-center py-3 space-y-2">
        {/* Gaming Hub icon */}
        <button
          className={`w-11 h-11 rounded-xl flex items-center justify-center cursor-pointer transition-all duration-200 ${
            isGamingHubActive
              ? 'bg-gaming-accent text-black shadow-lg shadow-gaming-accent/20'
              : 'bg-highlight hover:bg-highlight-hover text-text-primary hover:text-gaming-accent'
          }`}
          onClick={() => {
            setGamingHubActive(!isGamingHubActive);
            if (!isGamingHubActive) {
              setActiveSection('servers');
              setAdminConnectActive(false);
            }
          }}
          title={isElectron ? 'Gaming Hub' : 'Gaming Hub · installation disponible sur Desktop'}
        >
          <Gamepad2 className="h-5 w-5" />
        </button>

        {/* Unread DM avatars */}
        {unreadDms.filter(d => d.sender_id !== currentDmUser).map(dm => (
          <button
            key={dm.sender_id}
            className="relative w-11 h-11 rounded-xl overflow-visible flex-shrink-0 cursor-pointer transition-all duration-200 hover:rounded-2xl group"
            onClick={() => {
              handleFriendClick(dm.sender_id);
            }}
            title={dm.username}
          >
            <div className="w-11 h-11 rounded-xl overflow-hidden">
              {dm.avatar_url ? (
                <img src={dm.avatar_url} alt={dm.username} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-accent-color text-white flex items-center justify-center text-sm font-bold">
                  {dm.username.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <span className="absolute -bottom-1 -right-1 min-w-[18px] h-[18px] bg-error text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 border-2 border-bg-primary">
              {dm.unread_count > 99 ? '99+' : dm.unread_count}
            </span>
          </button>
        ))}

        <button 
          className={`w-11 h-11 rounded-xl flex items-center justify-center cursor-pointer transition-all duration-200 ${
            activeSection === 'friends' && !isGamingHubActive
              ? 'bg-accent-color text-black shadow-lg shadow-accent-color/20' 
              : 'bg-highlight hover:bg-highlight-hover text-text-primary'
          }`}
          onClick={() => { setActiveSection('friends'); setGamingHubActive(false); setCurrentServer(null); setCurrentChannel(null); }}
        >
          <Users className="h-5 w-5" />
        </button>
        
        <div className="w-8 h-0.5 bg-border-color rounded-full" />
        
        {/* Server list */}
        <div className="flex-1 overflow-y-auto overflow-x-visible flex flex-col items-center space-y-2 scrollbar-none px-2 pt-2 -mx-2"  style={{ scrollbarWidth: 'none' }}>
          {servers.map((server) => (
            <div
              key={server.id}
              onContextMenu={(e) => {
                e.preventDefault();
                setServerContextMenu({ position: { x: e.clientX, y: e.clientY }, serverId: server.id });
              }}
            >
              <ServerItem 
                server={server} 
                isActive={currentServer?.id === server.id && activeSection === 'servers'} 
                onClick={() => handleServerClick(server)} 
              />
            </div>
          ))}
        </div>
        
        <button 
          className="w-11 h-11 rounded-xl bg-highlight hover:bg-highlight-hover text-text-secondary hover:text-success flex items-center justify-center cursor-pointer transition-all duration-200"
          onClick={() => setShowCreateServer(true)}
          title="Créer un serveur"
        >
          <Plus className="h-5 w-5" />
        </button>
        
        {/* Bottom section: admin + mini profile */}
        <div className="mt-auto flex flex-col items-center gap-2 pt-2 border-t border-border-color w-full px-3">
          {isAdmin && (
            <button
              className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-200 ${
                isAdminConnectActive
                  ? 'bg-admin-accent/20 text-admin-accent ring-2 ring-admin-accent/40'
                  : 'bg-highlight hover:bg-highlight-hover text-accent-color'
              }`}
              onClick={() => {
                setAdminConnectActive(!isAdminConnectActive);
                if (!isAdminConnectActive) {
                  useGamingStore.getState().setGamingHubActive(false);
                }
              }}
              title="Admin Connect"
            >
              <Shield className="h-5 w-5" />
            </button>
          )}
          {/* Mini profile */}
          <button
            className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0 cursor-pointer transition-all duration-200 hover:ring-2 hover:ring-accent-color/50 relative group"
            onClick={() => setShowUserSettings(true)}
            title="Paramètres"
          >
            {profile?.avatar_url ? (
              <img src={profile.avatar_url} alt={profile.username} className="w-11 h-11 object-cover" />
            ) : (
              <div className="w-11 h-11 bg-highlight flex items-center justify-center text-text-primary font-bold text-sm">
                {profile?.username?.charAt(0).toUpperCase() || '?'}
              </div>
            )}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <Settings className="h-4 w-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </button>
        </div>
      </div>
      
      {/* Content sidebar — hidden when Gaming Hub or Admin Connect is active */}
      {!isGamingHubActive && !isAdminConnectActive && (
      <div className="w-60 bg-bg-secondary/80 backdrop-blur-sm border-l border-r border-border-color rounded-tl-lg overflow-hidden flex flex-col">
        <div className="flex-1 min-h-0 flex flex-col">
        {activeSection === 'servers' && currentServer ? (
          <div className="h-full flex flex-col">
            {/* Server header — Discord-style dropdown */}
            <div className="relative">
              <button
                className="w-full h-12 px-4 border-t border-b border-white/10 flex justify-between items-center hover:bg-highlight/30 transition-colors cursor-pointer"
                onClick={() => setShowServerDropdown(!showServerDropdown)}
              >
                <h2 className="font-bold text-base text-text-primary truncate">{currentServer.name}</h2>
                <ChevronDown className={`h-4 w-4 text-text-muted transition-transform duration-200 ${showServerDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown menu */}
              {showServerDropdown && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowServerDropdown(false)} />
                  <div className="absolute top-full left-2 right-2 mt-1 bg-bg-primary border border-border-color rounded-lg shadow-xl z-50 py-1.5 px-1.5 space-y-0.5">
                    {canCreateInvite && (
                      <button
                        className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm text-text-secondary hover:bg-highlight/50 hover:text-text-primary transition-colors"
                        onClick={() => { setShowCreateInvite(true); setShowServerDropdown(false); }}
                      >
                        <Link2 className="h-4 w-4" />
                        <span>Inviter des amis</span>
                      </button>
                    )}
                    {canAccessSettings && (
                      <button
                        className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm text-text-secondary hover:bg-highlight/50 hover:text-text-primary transition-colors"
                        onClick={() => { setShowServerSettings(true); setShowServerDropdown(false); }}
                      >
                        <Settings className="h-4 w-4" />
                        <span>Paramètres du serveur</span>
                      </button>
                    )}
                    <button
                      className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm text-text-secondary hover:bg-highlight/50 hover:text-text-primary transition-colors"
                      onClick={() => { onToggleMembers?.(); setShowServerDropdown(false); }}
                    >
                      <Users className="h-4 w-4" />
                      <span>Membres</span>
                    </button>
                    {developerMode && (
                      <button
                        className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm text-text-secondary hover:bg-highlight/50 hover:text-text-primary transition-colors"
                        onClick={() => {
                          navigator.clipboard.writeText(currentServer.id);
                          setShowServerDropdown(false);
                        }}
                      >
                        <Copy className="h-4 w-4" />
                        <span>Copy Guild ID</span>
                      </button>
                    )}
                    <div className="h-px bg-border-color/50 my-1 mx-1" />
                    {!isServerOwner && (
                    <button
                      className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm text-error hover:bg-error/10 transition-colors"
                      onClick={() => {
                        if (confirm('Quitter ce serveur ?')) {
                          leaveServer(currentServer.id);
                          setCurrentServer(null);
                          setCurrentChannel(null);
                          setActiveSection('friends');
                        }
                        setShowServerDropdown(false);
                      }}
                    >
                      <DoorOpen className="h-4 w-4" />
                      <span>Quitter le serveur</span>
                    </button>
                    )}
                  </div>
                </>
              )}
            </div>
            
            {/* Channel list */}
            <div
              className="p-2 overflow-y-auto flex-1 scrollbar-thin"
              onContextMenu={(e) => openChannelContextMenu(e)}
            >
              <div className="space-y-0.5">
                <div
                  className={`flex items-center px-2.5 py-1.5 rounded-lg cursor-pointer transition-all duration-150 group ${
                    !currentChannel ? 'bg-highlight text-text-primary' : 'text-text-secondary hover:bg-highlight/30 hover:text-text-primary'
                  }`}
                  onClick={() => {
                    setCurrentDmUser(null);
                    setCurrentChannel(null);
                  }}
                >
                  <HomeIcon className="h-4 w-4 mr-2 flex-shrink-0 text-text-muted" />
                  <span className="truncate text-sm font-medium">Home</span>
                </div>

                <TopLevelDropSlot index={0} />
                {topLevelItems.map((item, index) => (
                  <React.Fragment key={item.id}>
                    {item.type === 'category'
                      ? renderCategoryBlock(item, index, topLevelItems.length)
                      : renderChannelRow(item, index, null)}
                    <TopLevelDropSlot index={index + 1} />
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        ) : activeSection === 'friends' ? (
          <div className="h-full flex flex-col">
            {/* DM header */}
            <div className="h-12 px-4 border-t border-b border-white/10 flex justify-between items-center">
              <h2 className="font-bold text-base text-text-primary">Messages</h2>
              <button
                className="w-7 h-7 rounded-lg flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-highlight/50 transition-all"
                onClick={() => setShowNewDm(!showNewDm)}
                title="Nouveau message"
              >
                <PenSquare className="h-4 w-4" />
              </button>
            </div>

            {/* New DM input */}
            {showNewDm && (
              <div className="px-3 py-2 border-b border-border-color animate-fade-in">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Nom d'utilisateur..."
                    className="flex-1 bg-bg-primary/50 border border-border-color rounded-lg px-3 py-1.5 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-color/50 transition-colors"
                    value={newDmUsername}
                    onChange={(e) => setNewDmUsername(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleNewDm()}
                  />
                  <button
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-highlight/50 transition-all"
                    onClick={() => { setShowNewDm(false); setNewDmUsername(''); }}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {/* DM search */}
            <div className="px-3 py-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text-muted" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  className="w-full bg-bg-primary border border-white/10 rounded-lg pl-8 pr-3 py-1.5 text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-color/50 transition-colors"
                  value={dmSearch}
                  onChange={(e) => setDmSearch(e.target.value)}
                />
              </div>
              {/* Friends button */}
              <button
                className="w-full flex items-center gap-2.5 px-2.5 py-2 mt-2 rounded-lg text-sm font-medium text-text-secondary hover:bg-highlight/40 hover:text-text-primary transition-colors"
                onClick={() => { setActiveSection('friends'); setCurrentServer(null); setCurrentChannel(null); setCurrentDmUser(null); }}
              >
                <Users className="h-4 w-4 flex-shrink-0" />
                <span>Amis</span>
              </button>
            </div>

            <div className="h-px bg-border-color mx-3 mb-1" />
            
            {/* DM list */}
            <div className="px-2 overflow-y-auto flex-1 scrollbar-thin">
              <div className="space-y-0.5">
                {filteredConversations.map((convo) => {
                  const onlineStatus = friendStatusMap[convo.user_id];
                  const isOnline = onlineStatus === 'online';
                  return (
                    <div 
                      key={convo.user_id}
                      className={`flex items-center px-2.5 py-2 rounded-lg cursor-pointer transition-all duration-150 group ${
                        currentDmUser === convo.user_id 
                          ? 'bg-highlight text-text-primary' 
                          : 'hover:bg-highlight/30'
                      }`}
                      onClick={() => handleFriendClick(convo.user_id)}
                    >
                      {/* Avatar with status dot */}
                      <div className="relative flex-shrink-0 mr-2.5">
                        <div className="w-9 h-9 rounded-full overflow-hidden bg-highlight">
                          {convo.avatar_url ? (
                            <img 
                              src={convo.avatar_url} 
                              alt={convo.username} 
                              className="w-9 h-9 object-cover"
                            />
                          ) : (
                            <div className="w-9 h-9 bg-accent-color/80 text-white flex items-center justify-center font-bold text-sm">
                              {convo.username.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 ${
                          currentDmUser === convo.user_id ? 'border-highlight' : 'border-bg-secondary'
                        } ${isOnline ? 'bg-success' : 'bg-text-muted/40'}`} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className={`text-sm truncate ${convo.unread_count > 0 ? 'font-bold text-text-primary' : 'font-medium text-text-secondary'}`}>
                          {convo.username}
                        </div>
                        <div className="text-[11px] text-text-muted truncate leading-tight mt-0.5">
                          {convo.last_message
                            ? convo.last_message.length > 28
                              ? convo.last_message.substring(0, 28) + '…'
                              : convo.last_message
                            : ''}
                        </div>
                      </div>

                      {/* Unread badge */}
                      {convo.unread_count > 0 && (
                        <span className="ml-1.5 min-w-[18px] h-[18px] bg-error text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 flex-shrink-0">
                          {convo.unread_count > 99 ? '99+' : convo.unread_count}
                        </span>
                      )}
                    </div>
                  );
                })}
                
                {filteredConversations.length === 0 && conversations.length > 0 && (
                  <div className="text-text-muted text-xs p-3 text-center">
                    Aucun résultat
                  </div>
                )}

                {conversations.length === 0 && (
                  <div className="text-text-muted text-xs p-4 text-center">
                    Aucune conversation.<br/>Ajoutez des amis pour commencer!
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : servers.length > 0 ? (
          // Si des serveurs existent mais aucun n'est sélectionné, encourager à en choisir un
          <div className="h-full flex flex-col items-center justify-center p-4">
            <HomeIcon className="h-12 w-12 text-text-muted mb-4" />
            <p className="text-text-secondary text-center">
              Sélectionnez un serveur pour accéder à son accueil
            </p>
          </div>
        ) : (
          // Si aucun serveur n'existe, encourager à en créer un
          <div className="h-full flex flex-col items-center justify-center p-4">
            <Plus className="h-12 w-12 text-text-muted mb-4" />
            <p className="text-text-secondary text-center">
              Créez un serveur pour commencer à utiliser Erozion
            </p>
          </div>
        )}
        </div>
        {/* Persistent voice bar — always at the bottom when voice-connected */}
        <VoiceBar />
      </div>
      )}
      
      {showCreateServer && (
        <CreateServerModal onClose={() => setShowCreateServer(false)} />
      )}
      
      {showUserSettings && (
        <UserSettingsModal onClose={() => setShowUserSettings(false)} />
      )}
      
      {showServerSettings && currentServer && (
        <ServerSettingsModal
          serverId={currentServer.id}
          onClose={() => setShowServerSettings(false)}
        />
      )}
      
      {showRoleManagement && currentServer && (
        <RoleManagementModal
          serverId={currentServer.id}
          onClose={() => setShowRoleManagement(false)}
        />
      )}

      {showCreateInvite && currentServer && (
        <CreateInviteModal
          serverId={currentServer.id}
          serverName={currentServer.name}
          onClose={() => setShowCreateInvite(false)}
        />
      )}

      {createChannelDraft && currentServer && (
        <CreateChannelModal
          initialType={createChannelDraft.type}
          initialParentId={createChannelDraft.parentId}
          onClose={() => setCreateChannelDraft(null)}
        />
      )}
      {editingChannelId && (() => {
        const channel = channels.find((candidate) => candidate.id === editingChannelId);
        return channel ? (
          <EditChannelModal channel={channel} onClose={() => setEditingChannelId(null)} />
        ) : null;
      })()}
      {deletingChannelId && (() => {
        const channel = channels.find((candidate) => candidate.id === deletingChannelId);
        return channel ? (
          <DeleteChannelModal
            channel={channel}
            onClose={() => setDeletingChannelId(null)}
            onConfirm={() => deleteChannel(channel.id)}
          />
        ) : null;
      })()}

      {serverContextMenu && (() => {
        const srv = servers.find(s => s.id === serverContextMenu.serverId);
        if (!srv) return null;
        const isOwner = srv.owner_id === profile?.id;
        const items: ContextMenuItem[] = [
          {
            label: 'Invite People',
            icon: <Link2 className="h-4 w-4" />,
            onClick: () => {
              handleServerClick(srv);
              setShowCreateInvite(true);
            },
            hidden: !canCreateInvite,
          },
          {
            label: 'Server Settings',
            icon: <Settings className="h-4 w-4" />,
            onClick: () => {
              handleServerClick(srv);
              setShowServerSettings(true);
            },
            hidden: !isOwner && !canAccessSettings,
          },
          {
            label: 'Leave Server',
            icon: <DoorOpen className="h-4 w-4" />,
            onClick: async () => {
              if (window.confirm(`Are you sure you want to leave "${srv.name}"?`)) {
                try {
                  await leaveServer(srv.id);
                } catch (err) {
                  console.error('Failed to leave server:', err);
                }
              }
            },
            danger: true,
            hidden: isOwner,
            separator: true,
          },
        ];
        return (
          <ContextMenu
            items={items}
            position={serverContextMenu.position}
            onClose={() => setServerContextMenu(null)}
          />
        );
      })()}

      {channelContextMenu && (
        <ContextMenu
          items={[
            {
              label: 'Edit Channel',
              icon: <Settings className="h-4 w-4" />,
              onClick: () => {
                if (channelContextMenu.channelId) setEditingChannelId(channelContextMenu.channelId);
              },
              hidden: !canManageChannels || !channelContextMenu.channelId,
            },
            {
              label: 'Create Channel',
              icon: <Plus className="h-4 w-4" />,
              onClick: () => setCreateChannelDraft({ type: 'text', parentId: null }),
              hidden: !canManageChannels,
            },
            {
              label: 'Create Category',
              icon: <Plus className="h-4 w-4" />,
              onClick: () => setCreateChannelDraft({ type: 'category', parentId: null }),
              hidden: !canManageChannels,
            },
            {
              label: channelContextMenu.channelId && channels.find((candidate) => candidate.id === channelContextMenu.channelId)?.type === 'category' ? 'Delete Category' : 'Delete Channel',
              icon: <Trash2 className="h-4 w-4" />,
              onClick: () => {
                if (channelContextMenu.channelId) setDeletingChannelId(channelContextMenu.channelId);
              },
              hidden: !canManageChannels || !channelContextMenu.channelId,
              danger: true,
              separator: true,
            },
            {
              label: 'Copy Channel ID',
              icon: <Copy className="h-4 w-4" />,
              onClick: () => {
                if (channelContextMenu.channelId) {
                  navigator.clipboard.writeText(channelContextMenu.channelId);
                }
              },
              hidden: !developerMode || !channelContextMenu.channelId,
              separator: canManageChannels,
            },
          ]}
          position={channelContextMenu.position}
          onClose={() => setChannelContextMenu(null)}
        />
      )}
    </aside>
  );
};

export default Sidebar;
